import type { FieldDefinition, FieldType, SelectOption } from '@sentra/contracts';
import { MAX_NOTES_LENGTH } from '@sentra/contracts';
import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/design/components/checkbox';
import { Input } from '@/design/components/input';
import { Label } from '@/design/components/label';
import { NativeSelect } from '@/design/components/native-select';
import { Textarea } from '@/design/components/textarea';
import { CountryCombobox } from '@/features/persons/form/country-combobox';
import { dateBounds } from '@/features/persons/form/date-bounds';
import { PhoneInput } from '@/features/persons/form/phone-input';
import type { FormValue } from '@/features/persons/form/values';
import { helpOf, labelOf } from '@/features/persons/form/values';
import { useLocale } from '@/i18n';

/**
 * One field of the registration form, rendered from its definition.
 *
 * **A component per type, dispatched by a `switch` the compiler checks is total.** AD-16 makes core
 * and admin-defined fields the same thing, so there is no list of known fields anywhere in this
 * package: an administrator adds `talla_de_ropa` as a `select` and it renders, with its options, its
 * help text and its requirement level, with no code change. What makes that safe is that the type is
 * a closed set of eleven and the switch below is exhaustive — a twelfth type added to the contract
 * stops this file compiling rather than silently rendering nothing where a field belongs.
 *
 * **Every control is labelled and every refusal is attached to its own control**, through
 * `aria-invalid` and `aria-describedby`. A form that renders its problems in a list at the top is a
 * form where somebody with eleven fields open cannot tell which one is wrong.
 */
export function FieldControl({
  definition,
  value,
  problem,
  today,
  onChange,
}: Readonly<{
  definition: FieldDefinition;
  value: FormValue;
  /** The refusal for this field, already in the operator's language, or `null`. */
  problem: string | null;
  /** Today in the installation's reporting timezone, for the date bounds. Never read from a clock. */
  today: string;
  onChange: (next: FormValue) => void;
}>): ReactNode {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const controlId = useId();
  const problemId = `${controlId}-problem`;
  const helpId = `${controlId}-help`;
  const help = helpOf(definition, locale);
  const invalid = problem !== null;

  const describedBy =
    [problem === null ? null : problemId, help === null ? null : helpId]
      .filter((part): part is string => part !== null)
      .join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1.5" data-field={definition.key}>
      <Label id={`${controlId}-label`} htmlFor={controlId}>
        {labelOf(definition, locale)}
        {definition.requirement === 'required' && (
          <span aria-hidden className="text-destructive">
            {t('person.form.requiredMark')}
          </span>
        )}
        {definition.requirement === 'required' && (
          <span className="sr-only">{t('person.form.requiredLabel')}</span>
        )}
      </Label>

      <Control
        controlId={controlId}
        definition={definition}
        value={value}
        invalid={invalid}
        today={today}
        onChange={onChange}
        {...(describedBy === undefined ? {} : { describedBy })}
      />

      {help !== null && (
        <p id={helpId} className="text-xs text-muted-foreground">
          {help}
        </p>
      )}
      {problem !== null && (
        <p id={problemId} role="alert" className="text-xs text-destructive">
          {problem}
        </p>
      )}
    </div>
  );
}

interface ControlProps {
  readonly controlId: string;
  readonly definition: FieldDefinition;
  readonly value: FormValue;
  readonly invalid: boolean;
  readonly today: string;
  readonly describedBy?: string;
  readonly onChange: (next: FormValue) => void;
}

/** What a control holds when it holds text, whatever the stored shape is. */
function asText(value: FormValue): string {
  return typeof value === 'string' ? value : '';
}

/** What a multiselect holds. */
function asList(value: FormValue): readonly string[] {
  return typeof value === 'string' || typeof value === 'boolean' ? [] : value;
}

/**
 * The control for one field, chosen from a table keyed by type.
 *
 * **A table rather than a switch, and the difference is what the compiler can see.** `Record<
 * FieldType, ...>` makes a twelfth type added to the contract a missing key the compiler names, in
 * this file, at the line where the answer belongs — where a switch with a `default` would render the
 * new type as a plain text box and nothing would fail. It is also what keeps each control a
 * component of its own rather than an arm of one eleven-branch function nobody reads to the end.
 */
const CONTROLS: Readonly<Record<FieldType, (props: ControlProps) => ReactNode>> = {
  text: TextControl,
  textarea: LongTextControl,
  number: NumberControl,
  date: DateControl,
  boolean: BooleanControl,
  select: SelectControl,
  multiselect: MultiSelectControl,
  phone: PhoneControl,
  email: EmailControl,
  country: CountryControl,
  passport: PassportControl,
};

function Control(props: ControlProps): ReactNode {
  const Chosen = CONTROLS[props.definition.type];
  return <Chosen {...props} />;
}

/** The `aria-describedby` a control carries, as props to spread, or nothing. */
function described(describedBy: string | undefined): { 'aria-describedby'?: string } {
  return describedBy === undefined ? {} : { 'aria-describedby': describedBy };
}

function TextControl({
  controlId,
  value,
  invalid,
  describedBy,
  onChange,
}: ControlProps): ReactNode {
  return (
    <Input
      id={controlId}
      type="text"
      autoComplete="off"
      aria-invalid={invalid}
      {...described(describedBy)}
      value={asText(value)}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}

function NumberControl({
  controlId,
  value,
  invalid,
  describedBy,
  onChange,
}: ControlProps): ReactNode {
  return (
    <Input
      id={controlId}
      type="number"
      inputMode="decimal"
      aria-invalid={invalid}
      {...described(describedBy)}
      value={asText(value)}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}

function DateControl({
  controlId,
  definition,
  value,
  invalid,
  today,
  describedBy,
  onChange,
}: ControlProps): ReactNode {
  const bounds =
    definition.validation.type === 'date' ? dateBounds(definition.validation, today) : {};
  return (
    <Input
      id={controlId}
      type="date"
      aria-invalid={invalid}
      {...described(describedBy)}
      {...bounds}
      value={asText(value)}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}

function BooleanControl({
  controlId,
  value,
  invalid,
  describedBy,
  onChange,
}: ControlProps): ReactNode {
  return (
    <Checkbox
      id={controlId}
      aria-invalid={invalid}
      {...described(describedBy)}
      checked={value === true}
      onChange={(event) => {
        onChange(event.target.checked);
      }}
    />
  );
}

function EmailControl({
  controlId,
  value,
  invalid,
  describedBy,
  onChange,
}: ControlProps): ReactNode {
  return (
    <Input
      id={controlId}
      type="email"
      inputMode="email"
      autoComplete="off"
      aria-invalid={invalid}
      {...described(describedBy)}
      value={asText(value)}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}

/**
 * A document number, upper-cased as it is typed.
 *
 * That is what the stored value is: the contract's own validator upper-cases on the way in, so a
 * control that showed lower case would be showing something other than what the record will hold.
 */
function PassportControl({
  controlId,
  value,
  invalid,
  describedBy,
  onChange,
}: ControlProps): ReactNode {
  return (
    <Input
      id={controlId}
      type="text"
      autoComplete="off"
      aria-invalid={invalid}
      {...described(describedBy)}
      value={asText(value)}
      onChange={(event) => {
        onChange(event.target.value.toUpperCase());
      }}
      className="font-mono"
    />
  );
}

function PhoneControl({
  controlId,
  definition,
  value,
  invalid,
  describedBy,
  onChange,
}: ControlProps): ReactNode {
  return (
    <PhoneInput
      id={controlId}
      value={asText(value)}
      validation={
        definition.validation.type === 'phone' ? definition.validation : { type: 'phone' }
      }
      invalid={invalid}
      onChange={onChange}
      {...(describedBy === undefined ? {} : { describedBy })}
    />
  );
}

function CountryControl({
  controlId,
  value,
  invalid,
  describedBy,
  onChange,
}: ControlProps): ReactNode {
  return (
    <CountryCombobox
      id={controlId}
      value={asText(value)}
      invalid={invalid}
      onChange={onChange}
      {...(describedBy === undefined ? {} : { describedBy })}
    />
  );
}

function LongTextControl({
  controlId,
  definition,
  value,
  invalid,
  describedBy,
  onChange,
}: ControlProps): ReactNode {
  return (
    <LongText
      controlId={controlId}
      value={asText(value)}
      maxLength={
        definition.validation.type === 'textarea'
          ? (definition.validation.maxLength ?? MAX_NOTES_LENGTH)
          : MAX_NOTES_LENGTH
      }
      invalid={invalid}
      onChange={onChange}
      {...(describedBy === undefined ? {} : { describedBy })}
    />
  );
}

function SelectControl({
  controlId,
  definition,
  value,
  invalid,
  describedBy,
  onChange,
}: ControlProps): ReactNode {
  return (
    <SelectOne
      controlId={controlId}
      options={definition.validation.type === 'select' ? definition.validation.options : []}
      value={asText(value)}
      invalid={invalid}
      onChange={onChange}
      {...(describedBy === undefined ? {} : { describedBy })}
    />
  );
}

function MultiSelectControl({
  controlId,
  definition,
  value,
  invalid,
  describedBy,
  onChange,
}: ControlProps): ReactNode {
  return (
    <SelectMany
      controlId={controlId}
      options={definition.validation.type === 'multiselect' ? definition.validation.options : []}
      value={asList(value)}
      invalid={invalid}
      onChange={onChange}
      {...(describedBy === undefined ? {} : { describedBy })}
    />
  );
}

/**
 * A long-text control with a live character count.
 *
 * The count is rendered as "used of limit" rather than "remaining", and it is announced politely: an
 * operator writing a note about somebody needs to know they are near the end before they have
 * written past it, and a counter that only appears at the limit tells them after the fact.
 */
function LongText({
  controlId,
  value,
  maxLength,
  invalid,
  describedBy,
  onChange,
}: Readonly<{
  controlId: string;
  value: string;
  maxLength: number;
  invalid: boolean;
  describedBy?: string;
  onChange: (next: string) => void;
}>): ReactNode {
  const { t } = useTranslation();
  const countId = `${controlId}-count`;
  const described = [describedBy, countId].filter((part) => part !== undefined).join(' ');

  return (
    <div className="flex flex-col gap-1">
      <Textarea
        id={controlId}
        aria-invalid={invalid}
        aria-describedby={described}
        maxLength={maxLength}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      <p id={countId} aria-live="polite" className="self-end text-xs text-muted-foreground">
        {t('person.form.characterCount', { used: value.length, limit: maxLength })}
      </p>
    </div>
  );
}

function SelectOne({
  controlId,
  options,
  value,
  invalid,
  describedBy,
  onChange,
}: Readonly<{
  controlId: string;
  options: readonly SelectOption[];
  value: string;
  invalid: boolean;
  describedBy?: string;
  onChange: (next: string) => void;
}>): ReactNode {
  const { t } = useTranslation();
  const { locale } = useLocale();

  return (
    <NativeSelect
      id={controlId}
      aria-invalid={invalid}
      {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    >
      <option value="">{t('person.form.selectNone')}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label[locale]}
        </option>
      ))}
    </NativeSelect>
  );
}

/**
 * A multiselect, as a group of checkboxes.
 *
 * A checkbox group rather than a multi-select listbox, because the platform's own multi-select is
 * the one control every usability study finds people cannot operate: it needs a modifier key to add
 * a second choice and silently discards the first without one. Every option is visible and every
 * one is a tick.
 */
function SelectMany({
  controlId,
  options,
  value,
  invalid,
  describedBy,
  onChange,
}: Readonly<{
  controlId: string;
  options: readonly SelectOption[];
  value: readonly string[];
  invalid: boolean;
  describedBy?: string;
  onChange: (next: readonly string[]) => void;
}>): ReactNode {
  const { locale } = useLocale();

  return (
    // A group cannot be labelled by `htmlFor`, because a `div` is not a labelable element — so the
    // field's label is named here explicitly. Without it this is the one control in the form with
    // no accessible name, and its refusal is the one not attached to the thing that was refused.
    <div
      id={controlId}
      role="group"
      aria-labelledby={`${controlId}-label`}
      aria-invalid={invalid}
      {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
      className="flex flex-col gap-1.5"
    >
      {options.map((option) => {
        const checked = value.includes(option.value);
        return (
          <label key={option.value} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={checked}
              onChange={() => {
                onChange(
                  checked
                    ? value.filter((held) => held !== option.value)
                    : [...value, option.value],
                );
              }}
            />
            <span>{option.label[locale]}</span>
          </label>
        );
      })}
    </div>
  );
}
